import React, { useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AuthImg } from "@/components/AuthImg";
import { toast } from "sonner";
import { Edit3, Upload, Loader2, UserRoundCog } from "lucide-react";

export const FounderSection = ({ clan, canEdit, onChanged }) => {
  const { API } = useAuth();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    founder_name: clan.founder_name || "",
    founder_story: clan.founder_story || "",
    founder_photo_path: clan.founder_photo_path || null,
  });
  const fileRef = useRef(null);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(p => ({ ...p, founder_photo_path: data.path }));
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/clans/${clan.key}`, form);
      toast.success(t("saved"));
      setOpen(false);
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const hasFounder = clan.founder_name || clan.founder_story || clan.founder_photo_path;

  return (
    <section className="heritage-card p-6 mb-10 relative" data-testid="founder-section">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <UserRoundCog className="w-5 h-5" style={{ color: clan.color }} />
          <div className="label-uppercase" style={{ color: clan.color }}>{t("founder")}</div>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="border-[#993D22] text-[#993D22]" data-testid="edit-founder-btn">
                <Edit3 className="w-4 h-4 mr-1" /> {t("editFounder")}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#FAF7F2] max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif-heritage text-2xl text-[#261911]">{t("editFounder")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={save} className="space-y-4" data-testid="founder-form">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-[#F0E6D8] border-2 border-[#E3D5CA] flex items-center justify-center">
                    {form.founder_photo_path ? (
                      <AuthImg path={form.founder_photo_path} className="w-full h-full object-cover" alt="founder" fallback={<UserRoundCog className="w-8 h-8 text-[#993D22]" />} />
                    ) : (
                      <UserRoundCog className="w-8 h-8 text-[#993D22]" />
                    )}
                  </div>
                  <div>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="founder-photo-input" />
                    <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="border-[#993D22] text-[#993D22]" disabled={uploading} data-testid="upload-founder-photo-btn">
                      {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      {t("uploadPhoto")}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>{t("founderName")}</Label>
                  <Input value={form.founder_name} onChange={e => setForm(p => ({ ...p, founder_name: e.target.value }))} data-testid="founder-name-input" />
                </div>
                <div>
                  <Label>{t("founderStory")}</Label>
                  <Textarea rows={6} value={form.founder_story} onChange={e => setForm(p => ({ ...p, founder_story: e.target.value }))} data-testid="founder-story-input" />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button type="button" variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
                  <Button type="submit" className="btn-terra" disabled={saving} data-testid="save-founder-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {t("save")}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {!hasFounder ? (
        <p className="italic text-[#6E5B4F] text-sm" data-testid="founder-empty">{t("noFounderYet")}</p>
      ) : (
        <div className="flex flex-col sm:flex-row gap-5">
          <div className="w-28 h-28 rounded-full overflow-hidden bg-[#F0E6D8] border-2 border-[#E3D5CA] shrink-0 flex items-center justify-center">
            {clan.founder_photo_path ? (
              <AuthImg path={clan.founder_photo_path} alt={clan.founder_name || "founder"} className="w-full h-full object-cover" fallback={<UserRoundCog className="w-12 h-12 text-[#993D22]" />} />
            ) : (
              <UserRoundCog className="w-12 h-12 text-[#993D22]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            {clan.founder_name && (
              <h3 className="font-serif-heritage text-3xl text-[#261911]" data-testid="founder-name">{clan.founder_name}</h3>
            )}
            {clan.founder_story && (
              <p className="text-[#3A2312] whitespace-pre-wrap leading-relaxed mt-3" data-testid="founder-story">{clan.founder_story}</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default FounderSection;
